import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { usePageLoadMetric } from "../hooks/usePageLoadMetric";
import { Button } from "../components/atoms";
import {
    TagSelectorPanel,
    type TagMaster,
    type TagFormValues,
} from "../components/TagSelectorPanel";
import {
    fetchEmployeeTags,
    fetchEmployeeTagAssignments,
    createEmployeeTag,
    updateEmployeeTag,
    deleteEmployeeTag,
    updateEmployeeTagAssignments,
} from "../api/employeeTags";
import type { EmployeeFormOutletContext } from "../components/EmployeeEditLayout";
import { createEmployee, updateEmployee } from "../api/employees";
import { recordUserAction } from "../utils/awsRum";
import { ApiError } from "../utils/apiError";
import type { EmployeeFormData } from "../components/EmployeeForm";

/**
 * 従業員 > 4. タグ
 *
 * - 「設定を完了する」でタグ紐付けと基本情報（pendingFormState）の
 *   作成/更新をまとめて DB 反映する。
 */
const TagFormPage = () => {
    const navigate = useNavigate();
    const {
        setPageError,
        setInvalidFields,
        pendingFormState,
    } = useOutletContext<EmployeeFormOutletContext>();
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;
    const employeeId = id ? parseInt(id, 10) : null;

    const [tags, setTags] = useState<TagMaster[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tagError, setTagError] = useState<string | null>(null);

    usePageLoadMetric("tag-form", !loading);

    const loadTags = useCallback(async () => {
        const list = await fetchEmployeeTags();
        setTags(
            list.items.map((t) => ({
                id: t.id,
                name: t.name ?? "",
                color: t.color ?? "",
            })),
        );
    }, []);

    const loadAssignments = useCallback(async () => {
        if (!employeeId) return;
        const res = await fetchEmployeeTagAssignments(employeeId);
        setSelectedIds(res.tag_ids);
    }, [employeeId]);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            setTagError(null);
            try {
                await loadTags();
                if (isEdit) {
                    await loadAssignments();
                }
            } catch (e) {
                if (!cancelled) {
                    console.error("Failed to load tag data:", e);
                    setTagError(e instanceof Error ? e.message : "タグ情報の取得に失敗しました");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [isEdit, loadTags, loadAssignments]);

    const handleCreate = async (data: TagFormValues) => {
        await createEmployeeTag(data);
        await loadTags();
    };

    const handleUpdate = async (tagId: number, data: TagFormValues) => {
        await updateEmployeeTag(tagId, data);
        await loadTags();
    };

    const handleDelete = async (tagId: number) => {
        await deleteEmployeeTag(tagId);
        setSelectedIds((prev) => prev.filter((id) => id !== tagId));
        await loadTags();
    };

    const handleBack = () => {
        navigate(id ? `/employee/info/${id}/support` : "/employee/info/new/support");
    };

    const handleCancel = () => {
        navigate("/employee/info");
    };

    /**
     * 必須項目チェック。空フィールドがあれば setInvalidFields + setPageError。
     * @returns 全項目入力済みなら true
     */
    const validateRequired = (state: EmployeeFormData): boolean => {
        const missing: { key: string; label: string }[] = [];
        if (!state.code.trim()) missing.push({ key: "code", label: "ID" });
        if (!state.name.trim()) missing.push({ key: "name", label: "名前" });
        if (!state.store_id) missing.push({ key: "store_id", label: "店舗" });
        if (!state.department_id) missing.push({ key: "department_id", label: "部門" });
        if (!state.employment_type_id) missing.push({ key: "employment_type_id", label: "雇用形態" });
        if (missing.length > 0) {
            setInvalidFields(missing.map((f) => f.key));
            setPageError(`必須項目が入力されていません: ${missing.map((f) => f.label).join(", ")}`);
            return false;
        }
        return true;
    };

    const handleComplete = async () => {
        // 編集時で state 無し（サイドバーから直接来た等）: 基本情報の変更が無いのでタグ紐付けだけ保存
        if (isEdit && !pendingFormState) {
            if (!employeeId) {
                navigate("/employee/info");
                return;
            }
            setSaving(true);
            setPageError(null);
            setTagError(null);
            try {
                await updateEmployeeTagAssignments(employeeId, selectedIds);
                navigate("/employee/info");
            } catch (e) {
                console.error("Failed to save tag assignments:", e);
                setTagError(e instanceof Error ? e.message : "タグの保存に失敗しました");
            } finally {
                setSaving(false);
            }
            return;
        }

        // 新規・編集ともに state を検証してから DB 反映
        if (!pendingFormState) {
            setPageError("フォームデータが見つかりません。基本情報から入力し直してください。");
            return;
        }
        if (!validateRequired(pendingFormState)) return;

        setInvalidFields([]);
        setSaving(true);
        setPageError(null);
        setTagError(null);
        try {
            let targetEmployeeId: number | null = employeeId;
            if (isEdit && employeeId !== null) {
                recordUserAction("employee_updated");
                await updateEmployee(employeeId, {
                    code: pendingFormState.code,
                    name: pendingFormState.name,
                    nickname: pendingFormState.nickname || null,
                    birth_date: pendingFormState.birth_date,
                    is_foreign_national: pendingFormState.is_foreign_national,
                    memo: pendingFormState.memo || null,
                    employment_status: pendingFormState.employment_status,
                    store_id: pendingFormState.store_id,
                    department_id: pendingFormState.department_id,
                    employment_type_id: pendingFormState.employment_type_id,
                    permission_level: pendingFormState.permission_level,
                });
            } else {
                recordUserAction("employee_created");
                const created = await createEmployee({
                    code: pendingFormState.code,
                    name: pendingFormState.name,
                    nickname: pendingFormState.nickname || null,
                    birth_date: pendingFormState.birth_date,
                    is_foreign_national: pendingFormState.is_foreign_national,
                    memo: pendingFormState.memo || null,
                    employment_status: pendingFormState.employment_status,
                    store_id: pendingFormState.store_id,
                    department_id: pendingFormState.department_id,
                    employment_type_id: pendingFormState.employment_type_id,
                    permission_level: pendingFormState.permission_level,
                });
                targetEmployeeId = created.id;
            }
            if (targetEmployeeId !== null) {
                await updateEmployeeTagAssignments(targetEmployeeId, selectedIds);
            }
            navigate("/employee/info");
        } catch (err) {
            console.error("Failed to save employee:", err);
            const message = err instanceof Error ? err.message : (isEdit ? "更新に失敗しました" : "登録に失敗しました");
            setPageError(message);
            if (err instanceof ApiError) {
                recordUserAction("validation_error", {
                    form: "employee-form",
                    reason: "save_failed",
                    ...err.toLogDetail(),
                });
            } else {
                recordUserAction("validation_error", { form: "employee-form", reason: "save_failed", message });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex-1 flex bg-white rounded-lg overflow-hidden">
            <div className="flex-1 flex flex-col">
                <div className="flex-1 p-6 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <span className="text-gray-500">読み込み中...</span>
                        </div>
                    ) : (
                        <>
                            {!isEdit && (
                                <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 px-4 py-2 text-sm text-yellow-800">
                                    タグの紐付けは従業員の登録と同時に行われます。
                                </div>
                            )}
                            <TagSelectorPanel
                                tags={tags}
                                selectedIds={selectedIds}
                                onSelectedChange={setSelectedIds}
                                onCreate={handleCreate}
                                onUpdate={handleUpdate}
                                onDelete={handleDelete}
                                errorMessage={tagError}
                            />
                        </>
                    )}
                </div>

                <div className="flex justify-end gap-4 px-6 py-4">
                    <Button variant="primary" size="md" onClick={handleBack} className="px-8">
                        3.応援の設定に戻る
                    </Button>
                    <Button variant="outline" size="md" onClick={handleCancel} className="px-8">
                        キャンセル
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleComplete}
                        disabled={saving}
                        className="px-8"
                    >
                        {saving ? "保存中..." : isEdit ? "設定を完了する" : "登録する"}
                    </Button>
                </div>
            </div>

            <div className="w-px bg-gray-200 my-4" />
        </div>
    );
};

export default TagFormPage;
